import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Check, Copy, Eye, EyeOff, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

/** Excludes look-alike characters (0/O, 1/l/I) — these get read aloud and
 *  retyped by hand, so an ambiguous glyph turns into a failed login. */
const SAFE_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';

export function generateTempPassword(): string {
  const pick = () => SAFE_CHARS[Math.floor(Math.random() * SAFE_CHARS.length)];
  return `${Array.from({ length: 10 }, pick).join('')}!${Math.floor(Math.random() * 90 + 10)}`;
}

type Mode = 'generate' | 'manual';

interface SetTempPasswordDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userName: string;
  saving: boolean;
  /** Resolves true when the password was accepted, so the dialog knows to close. */
  onSubmit: (password: string) => Promise<boolean>;
}

/**
 * Chooses a temporary password for a user — generated or typed — and confirms
 * before setting it.
 *
 * The password is shown in full rather than masked by default: whoever sets it
 * has to pass it on, and a value they cannot read is of no use to them.
 *
 * @param userName - Whose password is being set, named in the dialog
 * @param onSubmit - Persists the password; resolve false to keep the dialog open
 */
export function SetTempPasswordDialog({
  open,
  onOpenChange,
  userName,
  saving,
  onSubmit,
}: SetTempPasswordDialogProps) {
  const [mode, setMode] = useState<Mode>('generate');
  const [password, setPassword] = useState('');
  const [revealed, setRevealed] = useState(true);
  const [copied, setCopied] = useState(false);

  // Start each visit from a clean slate with a password already suggested, so
  // the common case is open → Set.
  useEffect(() => {
    if (open) {
      setMode('generate');
      setPassword(generateTempPassword());
      setRevealed(true);
      setCopied(false);
    }
  }, [open]);

  const switchMode = (next: Mode) => {
    setMode(next);
    setPassword(next === 'generate' ? generateTempPassword() : '');
    setCopied(false);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — select the password and copy it manually');
    }
  };

  const submit = async () => {
    const ok = await onSubmit(password.trim());
    if (ok) onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Set a temporary password</DialogTitle>
          <DialogDescription>
            For {userName}. It applies immediately — share it securely, and they can
            change it in My Account.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={mode === 'generate' ? 'default' : 'outline'}
              onClick={() => switchMode('generate')}
            >
              Generate one
            </Button>
            <Button
              type="button"
              variant={mode === 'manual' ? 'default' : 'outline'}
              onClick={() => switchMode('manual')}
            >
              Enter my own
            </Button>
          </div>

          <div>
            <Label htmlFor="temp-password">Password</Label>
            <div className="mt-1 flex items-center gap-2">
              <Input
                id="temp-password"
                type={revealed ? 'text' : 'password'}
                value={password}
                readOnly={mode === 'generate'}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'manual' ? 'Type a password' : undefined}
                className="font-mono"
                autoFocus={mode === 'manual'}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setRevealed((v) => !v)}
                title={revealed ? 'Hide' : 'Show'}
              >
                {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={copy}
                disabled={!password}
                title="Copy"
              >
                {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              </Button>
              {mode === 'generate' && (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => { setPassword(generateTempPassword()); setCopied(false); }}
                  title="Generate a different one"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              {mode === 'generate'
                ? 'Avoids characters that are easily misread when passed on.'
                : 'Must meet the platform password rules.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || !password.trim()}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Set password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
